import React, { useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  useMediaQuery,
  Grid2 as Grid,
  CardActionArea,
  Typography,
  Button,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { renderFieldValue } from '../../utils/utils';
import { Actions, useDispatchProvider, useStateFormTransactionNew, useStateFormUser } from '../utils/StateProvider';
import { collection, doc, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useCollection } from 'react-firebase-hooks/firestore';
import ListHeaderLabel from '../utils/ListHeaderLabel';
import SkeletonList from '../utils/SkeletonList';
import ListNoResults from '../utils/ListNoResults';
import { createPortal } from 'react-dom';
import { usePermissions } from '../utils/AclProvider';

const ListUserTransactions = ({ portalRef }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const permissions = usePermissions();
  const dispatch = useDispatchProvider();
  const stateFormUser = useStateFormUser();
  const stateFormTransactionNew = useStateFormTransactionNew();
  const scrollContainerRef = useRef(null);
  const handleDialogTransaction = (transactionId) => () => {
    // dispatch && dispatch({ type: Actions.FormSupplier, payload: '' });
    // dispatch && dispatch({ type: Actions.FormCustomer, payload: '' });
    // dispatch && dispatch({ type: Actions.FormTransaction, payload: transactionId });
    dispatch &&
      dispatch({
        type: Actions.Forms,
        payload: {
          formOrder: '',
          formUser: stateFormUser,
          formCustomer: '',
          formSupplier: '',
          formTransaction: transactionId,
        },
      });
    dispatch && dispatch({ type: Actions.DialogTransaction, payload: true });
  };
  const columns = [
    { id: 'dateCreated', label: 'Date created' },
    { id: 'comments', label: 'Comments' },
    { id: 'reason', label: 'Reason' },
    { id: 'blackPayment', label: 'D.Note', align: 'right' },
    { id: 'invoicePayment', label: 'Invoice', align: 'right' },
    { id: 'taxesPayment', label: 'Taxes', align: 'right' },
    { id: 'downPayment', label: 'Down payment', align: 'right' },
    { id: 'error', label: 'Error', align: 'right' },
    { id: 'total', label: 'Total', align: 'right' },
    { id: 'sequenceTotal', label: 'Seq total', align: 'right' },
  ];

  // const collectionRef = query(collection(db, 'products'));
  // const collectionRef = collection(db, 'products');
  const userRef = doc(db, 'users', stateFormUser);
  const collectionRef = query(
    collection(db, 'transactions'),
    where('userRef', '==', userRef),
    orderBy('dateCreated', 'desc'),
  );

  const [transactionsSnapshot, loading, error] = useCollection(collectionRef, {
    snapshotListenOptions: { includeMetadataChanges: true },
  });

  useEffect(() => {
    if (error) {
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error fetching user transactions collection: ${error}`,
          },
        });
    }
  }, [error]);

  const mapColumnGridXs = (column) => {
    switch (column.id) {
      case 'dateCreated':
      case 'dateOrdered':
      case 'dateReceived':
      case 'productStatus':
        return 4;
      case 'supplierRef':
        return 8;
      default:
        return 12;
    }
  };

  const mapColumnGridMd = (column) => {
    switch (column.id) {
      case 'comments':
      case 'reason':
      case 'dateCreated':
      default:
        return 'grow';
    }
  };

  useEffect(() => {
    // Scroll to the specific transaction card when state changes
    if (!scrollContainerRef.current) return;
    if (!stateFormTransactionNew) return;
    if (loading) return;

    const container = scrollContainerRef.current;
    // Prefer scoping to the container to avoid unrelated matches
    const targetEl = document.getElementById(stateFormTransactionNew);

    // Find the first actually focusable element within the card (CardActionArea -> ButtonBase, buttons, links, inputs, etc.)
    const findFocusable = (root) => {
      if (!root) return null;
      const selector = [
        'button:not([disabled])',
        '[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '.MuiButtonBase-root',
        '[role="button"]',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');
      const el = root.querySelector(selector);
      if (!el) return null;
      const style = window.getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none') return null;
      return el;
    };
    const focusTarget = findFocusable(targetEl);

    if (targetEl && targetEl.scrollIntoView) {
      try {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        // Fallback without options for older environments
        targetEl.scrollIntoView();
      }

      const doFocus = () => {
        const toFocus = focusTarget || container;
        if (toFocus && document.activeElement !== toFocus && typeof toFocus.focus === 'function') {
          try {
            toFocus.focus();
          } catch (e) {
            // ignore
          }
        }
      };

      try {
        // Defer focus to the next frame and a short timeout to allow scroll to finish and content to mount
        requestAnimationFrame(() => {
          setTimeout(doFocus, 3000);
        });
      } catch (e) {
        setTimeout(doFocus, 3000);
      }
    }
  }, [stateFormTransactionNew, loading, transactionsSnapshot]);

  return (
    <Box
      component={'div'}
      sx={{ overflowY: 'auto', height: '100%' }}
      ref={scrollContainerRef}
      tabIndex={-1}
      role="region"
      aria-label="User transactions list"
    >
      <>
        {!isMobile && (
          <Box
            position={'sticky'}
            top={0}
            background={'background.surface'}
            zIndex={1}
          >
            <Card
              elevation={0}
              sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
            >
              <CardContent sx={{ flexGrow: 1, px: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Grid
                  container
                  spacing={2}
                >
                  {columns.map((column) => {
                    // if (stateFiltersSupplierProducts.columns[column.id]) {
                    return (
                      <Grid
                        size={mapColumnGridMd(column)}
                        key={column.id}
                      >
                        <ListHeaderLabel column={column} />
                      </Grid>
                    );
                    // } else {
                    //   return null;
                    // }
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
        <Grid
          container
          spacing={2}
        >
          {loading && <SkeletonList />}
          {!loading && transactionsSnapshot?.docs.length === 0 && <ListNoResults />}
          {/*{loading &&*/}
          {/*  stateFormSupplier &&*/}
          {/*  transactionsSnapshot?.docs.map((transaction) => {*/}
          {/*    const transactionData = transaction.data();*/}
          {/*    return (*/}
          {/*      <Grid*/}
          {/*        size={12}*/}
          {/*        key={transaction.id}*/}
          {/*      >*/}
          {/*        <Card sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }}>*/}
          {/*          <CardActionArea onClick={handleDialogTransaction(transaction.id)}>*/}
          {/*            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>*/}
          {/*              <Grid*/}
          {/*                container*/}
          {/*                spacing={2}*/}
          {/*              >*/}
          {/*                <Grid*/}
          {/*                  size={{*/}
          {/*                    xs: 12,*/}
          {/*                    md: 6,*/}
          {/*                  }}*/}
          {/*                >*/}
          {/*                  <Grid*/}
          {/*                    container*/}
          {/*                    spacing={2}*/}
          {/*                  >*/}
          {/*                    <Grid size={12}>*/}
          {/*                      <Typography*/}
          {/*                        color={'textSecondary'}*/}
          {/*                        variant={'body2'}*/}
          {/*                        align={isMobile ? 'left' : column.align}*/}
          {/*                      >*/}
          {/*                        {columns.find((col) => col.id === 'dateCreated').label}*/}
          {/*                      </Typography>*/}
          {/*                      {renderFieldValue(*/}
          {/*                        'dateCreated',*/}
          {/*                        transactionData['dateCreated'],*/}
          {/*                        isMobile ? 'left' : column.align,*/}
          {/*                      )}*/}
          {/*                    </Grid>*/}

          {/*                    <Grid*/}
          {/*                      size={{*/}
          {/*                        xs: 12,*/}
          {/*                        md: 6,*/}
          {/*                      }}*/}
          {/*                    >*/}
          {/*                      <Typography*/}
          {/*                        color={'textSecondary'}*/}
          {/*                        variant={'body2'}*/}
          {/*                        // align={isMobile ? 'left' : column.align}*/}
          {/*                      >*/}
          {/*                        {columns.find((col) => col.id === 'comments').label}*/}
          {/*                      </Typography>*/}
          {/*                      {renderFieldValue(*/}
          {/*                        'comments',*/}
          {/*                        transactionData['comments'],*/}
          {/*                        // isMobile ? 'left' : column.align,*/}
          {/*                      )}*/}
          {/*                    </Grid>*/}

          {/*                    <Grid*/}
          {/*                      size={{*/}
          {/*                        xs: 12,*/}
          {/*                        md: 6,*/}
          {/*                      }}*/}
          {/*                    >*/}
          {/*                      <Typography*/}
          {/*                        color={'textSecondary'}*/}
          {/*                        variant={'body2'}*/}
          {/*                        // align={isMobile ? 'left' : column.align}*/}
          {/*                      >*/}
          {/*                        {columns.find((col) => col.id === 'reason').label}*/}
          {/*                      </Typography>*/}
          {/*                      {renderFieldValue(*/}
          {/*                        'reason',*/}
          {/*                        transactionData['reason'],*/}
          {/*                        // isMobile ? 'left' : column.align,*/}
          {/*                      )}*/}
          {/*                    </Grid>*/}
          {/*                  </Grid>*/}
          {/*                </Grid>*/}
          {/*                <Grid*/}
          {/*                  size={{*/}
          {/*                    xs: 12,*/}
          {/*                    md: 6,*/}
          {/*                  }}*/}
          {/*                ></Grid>*/}
          {/*              </Grid>*/}
          {/*            </CardContent>*/}
          {/*          </CardActionArea>*/}
          {/*        </Card>*/}
          {/*      </Grid>*/}
          {/*    );*/}
          {/*  })}*/}
          {!loading &&
            stateFormUser &&
            transactionsSnapshot?.docs.map((transaction) => {
              return (
                <Grid
                  size={12}
                  key={transaction.id}
                >
                  <Card
                    id={transaction.id}
                    data-id={transaction.id}
                    sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }}
                  >
                    <CardActionArea onClick={handleDialogTransaction(transaction.id)}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Grid
                          container
                          spacing={2}
                        >
                          {columns.map((column) => {
                            // if (stateFiltersSupplierProducts.columns[column.id]) {
                            const transactionData = transaction.data();
                            return (
                              <Grid
                                size={{
                                  xs: mapColumnGridXs(column),
                                  md: mapColumnGridMd(column),
                                }}
                                key={column.id}
                              >
                                {isMobile && (
                                  <Typography
                                    color={'textSecondary'}
                                    variant={'body2'}
                                    align={isMobile ? 'left' : column.align}
                                  >
                                    {column.label}
                                  </Typography>
                                )}
                                {renderFieldValue(
                                  column.id,
                                  transactionData[column.id],
                                  isMobile ? 'left' : column.align,
                                )}
                              </Grid>
                            );
                            // } else {
                            //   return null;
                            // }
                          })}
                        </Grid>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
        </Grid>
        {permissions?.transactions?.create && portalRef.current
          ? createPortal(
              <Button
                color={'primary'}
                variant={'contained'}
                startIcon={<Add />}
                onClick={handleDialogTransaction('')}
                size={isMobile ? 'small' : 'large'}
              >
                Add transaction
              </Button>,
              // <Fab
              //   color="primary"
              //   aria-label="add"
              //   onClick={handleDialogProduct('')}
              // >
              //   <Add />
              // </Fab>,
              portalRef.current,
            )
          : null}
      </>
    </Box>
  );
};

export default ListUserTransactions;
